import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CAST_PORTRAIT, HERO_PORTRAIT } from "@/data/products";
import { optimizedImg, srcSetFor } from "@/lib/img";

const CAST = [
  {
    name: "Charles Brockman III",
    role: "The Substitute · Series Lead",
    image: HERO_PORTRAIT,
    bio: "Comedian, creator, and the only CB3. Star of every classroom he steps into.",
    link: "https://www.charlesbrockmaniii.com/",
  },
  {
    name: "The Know It Owls",
    role: "Class of '25 · Ensemble",
    image: CAST_PORTRAIT,
    bio: "Hand-picked classroom crew that runs the lessons whether the bell rings or not.",
    link: "https://wallofentertainment.com/",
  },
];

export default function CastTrailer() {
  return (
    <>
      {/* CAST */}
      <section
        id="cast"
        data-testid="cast-section"
        className="relative bg-[var(--st-cream)] py-16 md:py-24 border-b-2 border-[#111]"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-heading uppercase tracking-[0.22em] text-xs text-[var(--st-fg)]/60">
                Roll Call · 00
              </p>
              <h2 className="font-display uppercase leading-none tracking-tight text-[var(--st-maroon)] text-5xl md:text-7xl mt-2">
                MEET THE CLASS
              </h2>
            </div>
            <a
              href="https://wallofentertainment.com/"
              target="_blank"
              rel="noreferrer"
              data-testid="wall-of-ent-link"
              className="hidden md:inline-flex items-center gap-2 border-2 border-[#111] bg-[var(--st-gold)] px-3 py-2 font-heading uppercase tracking-[0.18em] text-xs shadow-brutal-sm hover:bg-[var(--st-orange)] transition-colors"
            >
              Wall of Entertainment <ArrowUpRight size={12} />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CAST.map((c, i) => (
              <motion.a
                key={c.name}
                href={c.link}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`cast-card-${i}`}
                className="group relative border-2 border-[#111] bg-[var(--st-bg)] shadow-brutal hover:-translate-y-1 transition-transform overflow-hidden block"
              >
                <div className="grid grid-cols-12 gap-0">
                  <div className="col-span-5 aspect-[3/4] overflow-hidden bg-[var(--st-muted)] border-r-2 border-[#111]">
                    <img
                      src={optimizedImg(c.image, 600)}
                      srcSet={srcSetFor(c.image, [320, 480, 640, 800])}
                      sizes="(max-width: 768px) 40vw, 25vw"
                      alt={c.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="col-span-7 p-5 flex flex-col justify-between">
                    <div>
                      <p className="font-heading uppercase tracking-[0.2em] text-[10px] text-[var(--st-orange)]">
                        {c.role}
                      </p>
                      <h3 className="font-display uppercase tracking-tight text-2xl md:text-3xl mt-1 text-[var(--st-maroon)]">
                        {c.name}
                      </h3>
                      <p className="mt-3 font-body text-sm leading-relaxed text-[var(--st-fg)]/80">
                        {c.bio}
                      </p>
                    </div>
                    <div className="mt-4 flex items-center gap-2 font-heading uppercase tracking-[0.18em] text-xs text-[var(--st-maroon)]">
                      Visit profile <ArrowUpRight size={12} />
                    </div>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
